<script setup lang="ts">
type Msg = {
  id: string;
  role: 'user' | 'model';
  text: string;
  at: number;
  provider?: string;
  model?: string;
};

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const { isFramed } = useDevicePreview();

/** Di preview HP/Tablet: chat ikut ke dalam frame, bukan nempel pojok desktop */
const teleportTo = computed(() => (isFramed.value ? '.jgi-device-app' : 'body'));

const loggedIn = ref(false);
const open = ref(false);
const sending = ref(false);
const draft = ref('');
const error = ref('');
const listEl = ref<HTMLElement | null>(null);
const inputEl = ref<HTMLTextAreaElement | null>(null);
const lastProvider = ref('');
const pendingRefNo = ref('');

const customWelcome = ref<{ id: string; en: string } | null>(null);
const customFabHover = ref<{ id: string; en: string } | null>(null);
const customStatus = ref<{ id: string; en: string } | null>(null);
const customPlaceholder = ref<{ id: string; en: string } | null>(null);
const customQuickTitle = ref<{ id: string; en: string } | null>(null);
const customSuggestions = ref<Array<{ id: string; en: string }> | null>(null);

function locKey() {
  return String(locale.value || 'id').startsWith('en') ? 'en' : 'id';
}

function pickLocale(pair: { id: string; en: string } | null | undefined, fallbackKey: string) {
  const custom = pair?.[locKey()]?.trim();
  return custom || t(fallbackKey);
}

const welcomeText = computed(() => pickLocale(customWelcome.value, 'tasya.welcome'));
const fabHoverText = computed(() => pickLocale(customFabHover.value, 'tasya.fabHover'));
const statusText = computed(() => pickLocale(customStatus.value, 'tasya.status'));
const placeholderText = computed(() => pickLocale(customPlaceholder.value, 'tasya.placeholder'));
const quickTitleText = computed(() => pickLocale(customQuickTitle.value, 'tasya.quickTitle'));

const SUGGESTION_ICONS = [
  'mdi-file-upload-outline',
  'mdi-office-building-outline',
  'mdi-percent-outline',
  'mdi-cash-fast',
  'mdi-scale-balance',
  'mdi-book-open-page-variant-outline',
] as const;

const messages = ref<Msg[]>([
  {
    id: 'welcome',
    role: 'model',
    text: '',
    at: Date.now(),
  },
]);

watch(
  welcomeText,
  (v) => {
    if (messages.value[0]?.id === 'welcome') messages.value[0].text = v;
  },
  { immediate: true }
);

function asPair(raw?: { id?: string; en?: string } | null) {
  if (!raw) return null;
  const id = String(raw.id || '');
  const en = String(raw.en || '');
  if (!id && !en) return null;
  return { id, en };
}

async function loadTasyaCopy() {
  if (!import.meta.client) return;
  try {
    const data = await $fetch<{
      welcome?: { id?: string; en?: string };
      fabHover?: { id?: string; en?: string };
      status?: { id?: string; en?: string };
      placeholder?: { id?: string; en?: string };
      quickTitle?: { id?: string; en?: string };
      suggestions?: Array<{ id?: string; en?: string }>;
    }>('/api/chat/tasya-settings');
    customWelcome.value = asPair(data?.welcome);
    customFabHover.value = asPair(data?.fabHover);
    customStatus.value = asPair(data?.status);
    customPlaceholder.value = asPair(data?.placeholder);
    customQuickTitle.value = asPair(data?.quickTitle);
    if (Array.isArray(data?.suggestions) && data.suggestions.length) {
      customSuggestions.value = data.suggestions.map((s) => ({
        id: String(s?.id || ''),
        en: String(s?.en || ''),
      }));
    } else {
      customSuggestions.value = null;
    }
  } catch {
    /* keep i18n fallback */
  }
}

function hideChat() {
  loggedIn.value = false;
  open.value = false;
  pendingRefNo.value = '';
}

async function checkAuth() {
  if (!import.meta.client) {
    hideChat();
    return;
  }
  const path = String(route.path || '');
  if (path.startsWith('/auth')) {
    hideChat();
    return;
  }

  // UX cache — wajib juga ada session server
  let hasLocal = false;
  try {
    hasLocal = Boolean(localStorage.getItem('jgi_auth'));
  } catch {
    hasLocal = false;
  }
  if (!hasLocal) {
    hideChat();
    return;
  }

  try {
    const me = await $fetch<{ authenticated?: boolean }>('/api/auth/me');
    if (!me?.authenticated) {
      hideChat();
      return;
    }
    loggedIn.value = true;
    void loadTasyaCopy();
  } catch {
    hideChat();
  }
}

let authPollTimers: number[] = [];

function onStorageAuth() {
  void checkAuth();
}
function onAuthChanged() {
  void checkAuth();
}
function onTasyaSettingsChanged() {
  void loadTasyaCopy();
}

onMounted(() => {
  void checkAuth();
  window.addEventListener('storage', onStorageAuth);
  window.addEventListener('jgi-auth-changed', onAuthChanged);
  window.addEventListener('jgi-tasya-settings', onTasyaSettingsChanged);
  window.addEventListener('tasya:ask', onTasyaAsk as EventListener);
  authPollTimers = [
    window.setTimeout(() => void checkAuth(), 400),
    window.setTimeout(() => void checkAuth(), 1500),
  ];
});

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('storage', onStorageAuth);
    window.removeEventListener('jgi-auth-changed', onAuthChanged);
    window.removeEventListener('jgi-tasya-settings', onTasyaSettingsChanged);
    window.removeEventListener('tasya:ask', onTasyaAsk as EventListener);
  }
  authPollTimers.forEach((id) => clearTimeout(id));
  authPollTimers = [];
});

watch(
  () => route.fullPath,
  () => void checkAuth()
);

async function scrollBottom() {
  await nextTick();
  const el = listEl.value;
  if (el) el.scrollTop = el.scrollHeight;
}

function toggle() {
  if (!loggedIn.value) return;
  open.value = !open.value;
  if (open.value) {
    nextTick(() => inputEl.value?.focus());
    void scrollBottom();
  }
}

function openWith(text: string, autoSend = false, refNo = '') {
  if (!loggedIn.value) return;
  open.value = true;
  draft.value = text;
  if (refNo) pendingRefNo.value = refNo;
  nextTick(() => {
    inputEl.value?.focus();
    if (autoSend && text.trim()) void send();
  });
}

function onTasyaAsk(e: Event) {
  if (!loggedIn.value) return;
  const detail = (e as CustomEvent).detail || {};
  const message = String(detail.message || '').trim();
  if (!message) return;
  openWith(message, Boolean(detail.autoSend), String(detail.refNo || '').trim());
}

defineExpose({ openWith, toggle });

function clearChat() {
  messages.value = [
    {
      id: 'welcome',
      role: 'model',
      text: welcomeText.value,
      at: Date.now(),
    },
  ];
  error.value = '';
  lastProvider.value = '';
  pendingRefNo.value = '';
}

/** Render teks jawaban: **bold**, path /menu, baris */
function renderRich(text: string) {
  const esc = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return esc
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(
      /(^|\s)(\/[a-z0-9][\w\-./]*)/gim,
      '$1<button type="button" class="tasya-path" data-path="$2">$2</button>'
    )
    .replace(/\n/g, '<br />');
}

function onBodyClick(e: MouseEvent) {
  const btn = (e.target as HTMLElement | null)?.closest?.('.tasya-path') as HTMLElement | null;
  if (!btn) return;
  const path = btn.getAttribute('data-path');
  if (!path) return;
  open.value = false;
  void router.push(path);
}

async function send() {
  if (!loggedIn.value) {
    hideChat();
    return;
  }
  const text = draft.value.trim();
  if (!text || sending.value) return;
  error.value = '';
  const userMsg: Msg = { id: `u-${Date.now()}`, role: 'user', text, at: Date.now() };
  messages.value.push(userMsg);
  draft.value = '';
  sending.value = true;
  void scrollBottom();

  try {
    const history = messages.value
      .filter((m) => m.id !== 'welcome')
      .slice(0, -1)
      .map((m) => ({ role: m.role, text: m.text }));

    const refNo = pendingRefNo.value;
    pendingRefNo.value = '';

    const res = await $fetch<{ reply: string; provider?: string; model?: string }>('/api/chat/tasya', {
      method: 'POST',
      body: {
        message: text,
        history,
        path: route.fullPath,
        locale: String(locale.value || 'id'),
        ...(refNo ? { refNo } : {}),
      },
    });

    lastProvider.value = res?.provider || '';
    messages.value.push({
      id: `a-${Date.now()}`,
      role: 'model',
      text: res?.reply || t('tasya.emptyReply'),
      at: Date.now(),
      provider: res?.provider,
      model: res?.model,
    });
  } catch (e: any) {
    const status = e?.statusCode || e?.status || e?.data?.statusCode;
    if (status === 401) {
      hideChat();
      try {
        localStorage.removeItem('jgi_auth');
      } catch {
        /* ignore */
      }
      error.value = t('tasya.needLogin');
      void router.push('/auth/login');
      return;
    }
    error.value = e?.data?.statusMessage || e?.message || t('tasya.error');
    messages.value.push({
      id: `e-${Date.now()}`,
      role: 'model',
      text: t('tasya.error'),
      at: Date.now(),
    });
  } finally {
    sending.value = false;
    void scrollBottom();
    nextTick(() => inputEl.value?.focus());
  }
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    void send();
  }
}

const quickPrompts = computed(() => {
  const fallbackKeys = ['tasya.q1', 'tasya.q2', 'tasya.q3', 'tasya.q4', 'tasya.q5', 'tasya.q6'] as const;
  return fallbackKeys.map((key, i) => {
    const custom = customSuggestions.value?.[i];
    const label = custom ? pickLocale(custom, key) : t(key);
    return { label, icon: SUGGESTION_ICONS[i] || 'mdi-help-circle-outline' };
  });
});

const pageHint = computed(() => {
  const p = String(route.path || '/');
  if (p === '/') return t('tasya.page.dash');
  if (p.startsWith('/statements')) return t('tasya.page.stmt');
  if (p.startsWith('/reconciliation')) return t('tasya.page.recon');
  if (p.startsWith('/tax')) return t('tasya.page.tax');
  if (p.startsWith('/accounts') || p.startsWith('/bank')) return t('tasya.page.master');
  if (p.startsWith('/reports')) return t('tasya.page.reports');
  if (p.startsWith('/journals')) return t('tasya.page.journals');
  return t('tasya.page.generic', { path: p });
});

function useQuick(q: string) {
  draft.value = q;
  void send();
}

function providerLabel(p?: string) {
  if (p === 'groq') return 'Groq';
  if (p === 'gemini') return 'Gemini';
  return '';
}

function formatTime(at: number) {
  try {
    return new Date(at).toLocaleTimeString(locale.value === 'en' ? 'en-GB' : 'id-ID', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
</script>

<template>
  <ClientOnly>
    <Teleport :to="teleportTo">
      <div
        v-if="loggedIn"
        class="tasya"
        :class="{ 'tasya--open': open, 'tasya--framed': isFramed }"
      >
        <Transition name="tasya-panel">
          <section v-if="open" class="tasya-panel" role="dialog" :aria-label="t('tasya.name')">
            <div class="tasya-panel__glow" aria-hidden="true" />

            <header class="tasya-panel__head">
              <div class="tasya-panel__identity">
                <span class="tasya-avatar" aria-hidden="true">
                  <span class="tasya-avatar__ring" />
                  <img class="tasya-avatar__img" src="/images/tasya/tasya-doll.png" alt="" width="44" height="44" />
                  <span class="tasya-avatar__dot" />
                </span>
                <div class="tasya-panel__titles">
                  <strong>{{ t('tasya.name') }}</strong>
                  <em>
                    <span class="tasya-live" />
                    {{ statusText }}
                    <span v-if="lastProvider" class="tasya-prov">· {{ providerLabel(lastProvider) }}</span>
                  </em>
                </div>
              </div>
              <div class="tasya-panel__actions">
                <button type="button" class="tasya-icon-btn tasya-icon-btn--clear" :title="t('tasya.clear')" @click="clearChat">
                  <v-icon size="18">mdi-broom</v-icon>
                </button>
                <button type="button" class="tasya-icon-btn tasya-icon-btn--close" :title="t('tasya.close')" @click="open = false">
                  <v-icon size="20">mdi-close</v-icon>
                </button>
              </div>
            </header>

            <div class="tasya-context">
              <v-icon size="14">mdi-map-marker-radius-outline</v-icon>
              <span>{{ pageHint }}</span>
            </div>

            <div ref="listEl" class="tasya-panel__body" @click="onBodyClick">
              <div
                v-for="m in messages"
                :key="m.id"
                class="tasya-row"
                :class="m.role === 'user' ? 'tasya-row--user' : 'tasya-row--bot'"
              >
                <div
                  class="tasya-bubble"
                  :class="m.role === 'user' ? 'tasya-bubble--user' : 'tasya-bubble--bot'"
                >
                  <div v-if="m.role === 'model'" class="tasya-bubble__meta">
                    <span class="tasya-bubble__tag">{{ t('tasya.name') }}</span>
                    <span v-if="m.provider" class="tasya-bubble__prov">{{ providerLabel(m.provider) }}</span>
                    <span class="tasya-bubble__time">{{ formatTime(m.at) }}</span>
                  </div>
                  <div
                    v-if="m.role === 'model'"
                    class="tasya-bubble__text"
                    v-html="renderRich(m.text)"
                  />
                  <p v-else class="tasya-bubble__text">{{ m.text }}</p>
                </div>
              </div>

              <div v-if="sending" class="tasya-typing">
                <img class="tasya-typing__img" src="/images/tasya/tasya-doll.png" alt="" width="28" height="28" />
                <div class="tasya-typing__dots">
                  <span /><span /><span />
                </div>
                <em>{{ t('tasya.thinking') }}</em>
              </div>

              <div v-if="messages.length <= 1" class="tasya-quick">
                <div class="tasya-quick__title">{{ quickTitleText }}</div>
                <button
                  v-for="q in quickPrompts"
                  :key="q.label"
                  type="button"
                  class="tasya-quick__chip"
                  @click="useQuick(q.label)"
                >
                  <v-icon size="15">{{ q.icon }}</v-icon>
                  <span>{{ q.label }}</span>
                </button>
              </div>
            </div>

            <footer class="tasya-panel__foot">
              <p v-if="error" class="tasya-panel__err">{{ error }}</p>
              <div class="tasya-composer">
                <textarea
                  ref="inputEl"
                  v-model="draft"
                  rows="2"
                  class="tasya-composer__input"
                  :placeholder="placeholderText"
                  :disabled="sending"
                  @keydown="onKey"
                />
                <button
                  type="button"
                  class="tasya-composer__send"
                  :disabled="sending || !draft.trim()"
                  :aria-label="t('tasya.send')"
                  @click="send"
                >
                  <v-icon v-if="!sending" size="20">mdi-send</v-icon>
                  <v-progress-circular v-else indeterminate size="18" width="2" color="white" />
                </button>
              </div>
              <p class="tasya-panel__hint">{{ t('tasya.hint') }}</p>
            </footer>
          </section>
        </Transition>

        <button
          type="button"
          class="tasya-fab"
          :class="{ 'tasya-fab--open': open }"
          :aria-expanded="open"
          :aria-label="open ? t('tasya.close') : t('tasya.open')"
          @click="toggle"
        >
          <template v-if="!open">
            <span class="tasya-fab__halo" aria-hidden="true" />
            <img
              class="tasya-fab__doll"
              src="/images/tasya/tasya-doll.png"
              alt=""
              width="60"
              height="60"
              decoding="async"
            />
            <span class="tasya-fab__bubble">{{ fabHoverText }}</span>
          </template>
          <span v-else class="tasya-fab__xwrap" aria-hidden="true">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </button>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.tasya {
  position: fixed;
  right: 20px;
  bottom: 22px;
  z-index: 10050;
  font-family: 'Segoe UI', system-ui, sans-serif;
  pointer-events: none;
}
.tasya--framed {
  position: absolute;
  right: 10px;
  bottom: 12px;
  z-index: 2500;
}
.tasya > * {
  pointer-events: auto;
}

.tasya-fab {
  position: relative;
  width: 64px;
  height: 64px;
  border: 3px solid #fff;
  border-radius: 50%;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
  background: linear-gradient(160deg, #ffffff 0%, #e0f2fe 55%, #ccfbf1 100%);
  color: #0f172a;
  box-shadow:
    0 10px 24px rgba(14, 165, 233, 0.28),
    0 2px 6px rgba(15, 23, 42, 0.08);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;
  overflow: visible;
}
.tasya-fab:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow:
    0 14px 30px rgba(14, 165, 233, 0.36),
    0 2px 8px rgba(15, 23, 42, 0.1);
}
.tasya-fab__halo {
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.22) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
  animation: tasya-soft 2.8s ease-in-out infinite;
}
@keyframes tasya-soft {
  0%,
  100% {
    opacity: 0.55;
    transform: scale(0.96);
  }
  50% {
    opacity: 1;
    transform: scale(1.04);
  }
}
.tasya-fab__doll {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  z-index: 2;
  pointer-events: none;
  display: block;
}
/* Saat chat terbuka: tombol tutup berwarna jelas (bukan X putih aneh) */
.tasya-fab--open {
  width: 52px;
  height: 52px;
  border: 0;
  background: linear-gradient(145deg, #fb7185 0%, #e11d48 55%, #be123c 100%);
  box-shadow:
    0 10px 22px rgba(225, 29, 72, 0.4),
    0 1px 0 rgba(255, 255, 255, 0.25) inset;
}
.tasya-fab--open:hover {
  box-shadow: 0 14px 28px rgba(225, 29, 72, 0.48);
}
.tasya-fab__xwrap {
  display: grid;
  place-items: center;
  color: #fff;
  z-index: 2;
}
.tasya-fab__bubble {
  position: absolute;
  right: 74px;
  bottom: 4px;
  width: min(248px, calc(100vw - 110px));
  white-space: pre-line;
  padding: 10px 12px;
  border-radius: 16px 16px 4px 16px;
  background: linear-gradient(145deg, #0f172a 0%, #1e3a8a 100%);
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1.35;
  text-align: left;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.28);
  opacity: 0;
  transform: translateX(8px) scale(0.96);
  pointer-events: none;
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
  z-index: 5;
}
.tasya-fab__bubble::after {
  content: '';
  position: absolute;
  right: -6px;
  bottom: 14px;
  width: 12px;
  height: 12px;
  background: #1e3a8a;
  transform: rotate(45deg);
  border-radius: 2px;
}
.tasya-fab:hover .tasya-fab__bubble {
  opacity: 1;
  transform: none;
}
.tasya-fab--open .tasya-fab__bubble {
  display: none;
}

.tasya-panel {
  position: absolute;
  right: 0;
  bottom: 78px;
  width: min(420px, calc(100vw - 24px));
  height: min(640px, calc(100dvh - 100px));
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  isolation: isolate;
  background: linear-gradient(165deg, #f0f9ff 0%, #ffffff 28%, #f8fafc 100%);
  border: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow:
    0 28px 64px rgba(15, 23, 42, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.65) inset;
}
.tasya-panel__glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 80% 40% at 10% -10%, rgba(56, 189, 248, 0.28), transparent 55%),
    radial-gradient(ellipse 70% 35% at 100% 0%, rgba(45, 212, 191, 0.18), transparent 50%);
  z-index: 0;
}
.tasya-panel > *:not(.tasya-panel__glow) {
  position: relative;
  z-index: 1;
}

.tasya-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 14px 12px;
  background: linear-gradient(125deg, #0b1220 0%, #1e3a8a 48%, #0f766e 115%);
  color: #fff;
}
.tasya-panel__identity {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}
.tasya-panel__titles strong {
  display: block;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
}
.tasya-panel__titles em {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-style: normal;
  font-size: 11px;
  font-weight: 650;
  opacity: 0.88;
  margin-top: 2px;
}
.tasya-live {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.25);
  animation: tasya-live 1.6s ease-in-out infinite;
}
@keyframes tasya-live {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
.tasya-prov {
  opacity: 0.95;
  font-weight: 700;
}
.tasya-avatar {
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}
.tasya-avatar__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(from 200deg, #38bdf8, #2dd4bf, #818cf8, #38bdf8);
  animation: tasya-spin 8s linear infinite;
}
@keyframes tasya-spin {
  to {
    transform: rotate(360deg);
  }
}
.tasya-avatar__face {
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #0f172a;
  display: grid;
  place-items: center;
  font-weight: 900;
  font-size: 17px;
}
.tasya-avatar__img {
  position: absolute;
  inset: 3px;
  width: calc(100% - 6px);
  height: calc(100% - 6px);
  border-radius: 50%;
  object-fit: cover;
  background: #ecfeff;
  z-index: 1;
}
.tasya-avatar__dot {
  position: absolute;
  right: 1px;
  bottom: 1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #0f172a;
  z-index: 2;
}
.tasya-panel__actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.tasya-icon-btn {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    filter 0.15s ease;
}
.tasya-icon-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.06);
}
.tasya-icon-btn--clear {
  background: linear-gradient(145deg, #fbbf24, #f59e0b);
  color: #78350f;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
}
.tasya-icon-btn--close {
  background: linear-gradient(145deg, #fb7185, #e11d48);
  color: #fff;
  box-shadow: 0 4px 14px rgba(225, 29, 72, 0.4);
}
.tasya-icon-btn--close:hover {
  box-shadow: 0 6px 16px rgba(225, 29, 72, 0.5);
}

.tasya-context {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  font-size: 11.5px;
  font-weight: 700;
  color: #1e40af;
  background: linear-gradient(90deg, rgba(219, 234, 254, 0.95), rgba(204, 251, 241, 0.75));
  border-bottom: 1px solid rgba(147, 197, 253, 0.45);
}

.tasya-panel__body {
  flex: 1;
  overflow: auto;
  padding: 14px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-gutter: stable;
}

.tasya-row {
  display: flex;
  width: 100%;
}
.tasya-row--bot {
  justify-content: flex-start;
}
.tasya-row--user {
  justify-content: flex-end;
}

.tasya-bubble {
  max-width: 92%;
  padding: 11px 13px;
  border-radius: 18px;
  font-size: 13.2px;
  line-height: 1.5;
  font-weight: 600;
  word-break: break-word;
  animation: tasya-pop 0.22s ease;
}
@keyframes tasya-pop {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
.tasya-bubble--bot {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e2e8f0;
  color: #0f172a;
  border-bottom-left-radius: 7px;
  box-shadow:
    0 8px 20px rgba(15, 23, 42, 0.05),
    0 1px 0 rgba(255, 255, 255, 0.9) inset;
  backdrop-filter: blur(6px);
}
.tasya-bubble--user {
  background: linear-gradient(135deg, #1d4ed8 0%, #0284c7 55%, #0f766e 130%);
  color: #fff;
  border-bottom-right-radius: 7px;
  box-shadow: 0 10px 22px rgba(29, 78, 216, 0.22);
}
.tasya-bubble__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.tasya-bubble__tag {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #0284c7;
}
.tasya-bubble__prov {
  font-size: 10px;
  font-weight: 800;
  color: #0f766e;
  background: #ecfdf5;
  border-radius: 999px;
  padding: 1px 7px;
}
.tasya-bubble__time {
  margin-left: auto;
  font-size: 10px;
  font-weight: 650;
  color: #94a3b8;
}
.tasya-bubble__text {
  margin: 0;
}
.tasya-bubble__text :deep(strong) {
  font-weight: 800;
  color: inherit;
}
.tasya-bubble__text :deep(.tasya-path) {
  display: inline;
  border: 0;
  padding: 0 2px;
  margin: 0;
  background: transparent;
  color: #1d4ed8;
  font: inherit;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.tasya-bubble--user .tasya-bubble__text {
  color: #fff;
}

.tasya-typing {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  align-self: flex-start;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}
.tasya-typing__face {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0f172a;
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 900;
}
.tasya-typing__img {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0f2fe;
  box-shadow: 0 2px 6px rgba(14, 165, 233, 0.2);
}
.tasya-typing__dots {
  display: inline-flex;
  gap: 4px;
}
.tasya-typing__dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #64748b;
  animation: tasya-dot 1s infinite ease-in-out;
}
.tasya-typing__dots span:nth-child(2) {
  animation-delay: 0.15s;
}
.tasya-typing__dots span:nth-child(3) {
  animation-delay: 0.3s;
}
.tasya-typing em {
  font-style: normal;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}
@keyframes tasya-dot {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.tasya-quick {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
  padding: 12px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(239, 246, 255, 0.95));
  border: 1px dashed #bfdbfe;
}
.tasya-quick__title {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}
.tasya-quick__chip {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #bfdbfe;
  background: #fff;
  color: #1e3a8a;
  border-radius: 14px;
  padding: 9px 11px;
  font-size: 12.5px;
  font-weight: 750;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.15s ease,
    transform 0.15s ease,
    border-color 0.15s ease;
}
.tasya-quick__chip:hover {
  background: #eff6ff;
  border-color: #60a5fa;
  transform: translateX(2px);
}

.tasya-panel__foot {
  padding: 10px 12px 12px;
  border-top: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(10px);
}
.tasya-panel__err {
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: #be123c;
}
.tasya-composer {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}
.tasya-composer__input {
  flex: 1;
  min-height: 52px;
  max-height: 110px;
  resize: none;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  padding: 11px 13px;
  font-size: 13.2px;
  font-weight: 600;
  color: #0f172a;
  background: #fff;
  font-family: inherit;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.8) inset;
}
.tasya-composer__input:focus {
  outline: 2px solid rgba(37, 99, 235, 0.22);
  border-color: #60a5fa;
}
.tasya-composer__send {
  width: 48px;
  height: 48px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #1d4ed8, #0f766e);
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  box-shadow: 0 8px 18px rgba(29, 78, 216, 0.28);
}
.tasya-composer__send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}
.tasya-panel__hint {
  margin: 7px 2px 0;
  font-size: 10.5px;
  font-weight: 650;
  color: #94a3b8;
}

.tasya-panel-enter-active,
.tasya-panel-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.tasya-panel-enter-from,
.tasya-panel-leave-to {
  opacity: 0;
  transform: translateY(14px) scale(0.96);
}

@media (max-width: 480px) {
  .tasya {
    right: 10px;
    bottom: 10px;
  }
  .tasya-panel {
    width: calc(100vw - 20px);
    height: min(78dvh, 620px);
    border-radius: 20px;
  }
  .tasya-fab__bubble {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
  .tasya-fab__halo {
    display: none !important;
  }
  .tasya-fab {
    overflow: hidden;
  }
}

:global(html.jgi-device-hp .tasya-fab__bubble),
:global(.jgi-device-studio--hp .tasya-fab__bubble),
:global(html.jgi-device-hp .tasya-fab__halo),
:global(.jgi-device-studio--hp .tasya-fab__halo) {
  display: none !important;
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}
:global(html.jgi-device-hp .tasya-fab),
:global(.jgi-device-studio--hp .tasya-fab) {
  overflow: hidden !important;
  background: #fff !important;
}

/* ——— Dark mode ——— */
:global(html.jgi-dark .tasya-fab){
  border-color: #1e293b;
  background: linear-gradient(160deg, #1e293b 0%, #0f172a 60%, #134e4a 120%);
  box-shadow:
    0 10px 24px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(56, 189, 248, 0.25);
}
:global(html.jgi-dark .tasya-fab--open){
  border: 0;
  background: linear-gradient(145deg, #fb7185 0%, #e11d48 55%, #9f1239 100%);
  box-shadow: 0 10px 22px rgba(225, 29, 72, 0.45);
}
:global(html.jgi-dark .tasya-fab__bubble){
  background: linear-gradient(145deg, #020617 0%, #1e3a8a 100%);
  border: 1px solid rgba(148, 163, 184, 0.25);
}
:global(html.jgi-dark .tasya-panel){
  background: linear-gradient(165deg, #0f172a 0%, #111827 40%, #0b1220 100%);
  border-color: rgba(148, 163, 184, 0.22);
  box-shadow:
    0 28px 64px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}
:global(html.jgi-dark .tasya-panel__glow){
  background:
    radial-gradient(ellipse 80% 40% at 10% -10%, rgba(56, 189, 248, 0.16), transparent 55%),
    radial-gradient(ellipse 70% 35% at 100% 0%, rgba(45, 212, 191, 0.1), transparent 50%);
}
:global(html.jgi-dark .tasya-context){
  color: #93c5fd;
  background: linear-gradient(90deg, rgba(30, 58, 138, 0.45), rgba(15, 118, 110, 0.28));
  border-bottom-color: rgba(51, 65, 85, 0.9);
}
:global(html.jgi-dark .tasya-panel__body){
  background: transparent;
}
:global(html.jgi-dark .tasya-bubble--bot){
  background: rgba(30, 41, 59, 0.92);
  border-color: #334155;
  color: #e2e8f0;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}
:global(html.jgi-dark .tasya-bubble__tag){
  color: #38bdf8;
}
:global(html.jgi-dark .tasya-bubble__prov){
  background: rgba(15, 118, 110, 0.35);
  color: #5eead4;
}
:global(html.jgi-dark .tasya-bubble__time){
  color: #64748b;
}
:global(html.jgi-dark .tasya-bubble__text) :deep(.tasya-path){
  color: #7dd3fc;
}
:global(html.jgi-dark .tasya-typing){
  background: #1e293b;
  border-color: #334155;
}
:global(html.jgi-dark .tasya-typing em){
  color: #94a3b8;
}
:global(html.jgi-dark .tasya-quick){
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95));
  border-color: #334155;
}
:global(html.jgi-dark .tasya-quick__title){
  color: #94a3b8;
}
:global(html.jgi-dark .tasya-quick__chip){
  background: #0f172a;
  border-color: #334155;
  color: #bfdbfe;
}
:global(html.jgi-dark .tasya-quick__chip:hover){
  background: #1e293b;
  border-color: #38bdf8;
}
:global(html.jgi-dark .tasya-panel__foot){
  background: rgba(15, 23, 42, 0.92);
  border-top-color: #1e293b;
}
:global(html.jgi-dark .tasya-composer__input){
  background: #0f172a;
  border-color: #334155;
  color: #e2e8f0;
  box-shadow: none;
}
:global(html.jgi-dark .tasya-composer__input:focus){
  border-color: #38bdf8;
  outline-color: rgba(56, 189, 248, 0.25);
}
:global(html.jgi-dark .tasya-composer__input::placeholder){
  color: #64748b;
}
:global(html.jgi-dark .tasya-panel__hint){
  color: #64748b;
}
</style>
